import React from 'react';
import { Card, Row, Col, Statistic, Alert, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type {
  ObserveComparison,
  ObserveComparisonMatched,
  ObserveComparisonMissing,
  ObserveComparisonUnexpected,
} from '../../../../services/devApi';
import { fmtOps } from '../../hooks/useFlowObserve';

interface Props {
  comparison: ObserveComparison;
}

const ComparisonSection: React.FC<Props> = ({ comparison }) => {
  const matched = comparison.matched || [];
  const missing = comparison.missing || [];
  const unexpected = comparison.unexpected || [];
  const allGood = missing.length === 0 && unexpected.length === 0;

  const matchedCols: ColumnsType<ObserveComparisonMatched> = [
    { title: '表', dataIndex: 'table', key: 'table', render: (v: string) => <span className="font-mono">{v}</span> },
    { title: 'Expected Ops', dataIndex: 'expectedOps', key: 'expectedOps', width: 120, render: fmtOps },
    { title: 'Actual Ops', dataIndex: 'actualOps', key: 'actualOps', width: 120, render: fmtOps },
  ];

  const missingCols: ColumnsType<ObserveComparisonMissing> = [
    { title: '表', dataIndex: 'table', key: 'table', render: (v: string) => <span className="font-mono">{v}</span> },
    { title: 'Expected Ops', dataIndex: 'expectedOps', key: 'expectedOps', width: 120, render: fmtOps },
    { title: '本應發生', dataIndex: 'note', key: 'note' },
  ];

  const unexpectedCols: ColumnsType<ObserveComparisonUnexpected> = [
    { title: '表', dataIndex: 'table', key: 'table', render: (v: string) => <span className="font-mono">{v}</span> },
    { title: '來源', dataIndex: 'source', key: 'source', width: 100 },
    { title: '新增', dataIndex: 'inserts', key: 'inserts', width: 70, align: 'center' },
    { title: '更新', dataIndex: 'updates', key: 'updates', width: 70, align: 'center' },
    { title: '刪除', dataIndex: 'deletes', key: 'deletes', width: 70, align: 'center' },
  ];

  return (
    <Card title="Expected vs Actual 比對結論">
      <Row gutter={16} className="mb-4">
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="吻合" value={matched.length} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="缺寫" value={missing.length} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="未知多寫" value={unexpected.length} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
      </Row>

      {allGood ? (
        <Alert
          type="success"
          showIcon
          message={<span style={{ fontSize: 16, fontWeight: 600 }}>✅ Actual Write Set 與 Expected 完全吻合</span>}
          className="mb-4"
        />
      ) : (
        <>
          {missing.length > 0 && (
            <Alert
              type="error"
              showIcon
              message={`缺寫 ${missing.length} 張表`}
              description={missing.map((m) => m.table).join('、')}
              className="mb-2"
            />
          )}
          {unexpected.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`白名單外多寫 ${unexpected.length} 張表`}
              description={unexpected.map((u) => u.table).join('、')}
              className="mb-4"
            />
          )}
        </>
      )}

      <Row gutter={16}>
        <Col xs={24} lg={8}>
          <Card size="small" title={`吻合 (${matched.length})`}>
            <Table<ObserveComparisonMatched>
              dataSource={matched}
              columns={matchedCols}
              rowKey="table"
              size="small"
              pagination={false}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title={`缺寫 (${missing.length})`}>
            <Table<ObserveComparisonMissing>
              dataSource={missing}
              columns={missingCols}
              rowKey="table"
              size="small"
              pagination={false}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title={`未知多寫 (${unexpected.length})`}>
            <Table<ObserveComparisonUnexpected>
              dataSource={unexpected}
              columns={unexpectedCols}
              rowKey="table"
              size="small"
              pagination={false}
            />
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default ComparisonSection;
